import Modal from "@/components/controls/Modal";
import React, { Suspense, lazy, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Text from "@/components/ui/text";
import { Button } from "@/components/ui/button";
import FormNew from "@/components/controls/Form/FormNew";
import { FormSectionProps } from "@/components/controls/Form/FormSection";
import { FormFieldNewType } from "@/components/controls/Form/types";
import { Close } from "@dreamhub/designsystem/icons";
import toast from "react-hot-toast";
import { UserData } from "@/entities/users/types";
import { PartnerContactData } from "../types";
import { getPartnerContactFullName } from "../helpers";
import { useCreatePartnerContactMutation } from "../hooks";
import { EntityType } from "@/services/hooks/entities/useEntityType";
import { mapEmptyToNull } from "@/utils/formatting";
import { useEntityFields } from "@/pages/settings/hooks";
import {
  createFormNewSectionsFromAPI,
  CreateFormNewContext,
  buildMapEmptyToNullObject
} from "@/utils/entityFieldsUtils/formFields";
import useBoundStore from "@/services/store/BoundStore";
import { useUnsavedChanges } from "@/hooks/useUnsavedChanges";
import UnsavedChangesWarningDialog from "@/components/layouts/UnsavedChangesWarningDialog";
import { createUnsavedChangesModalSetter } from "@/entities/modals/helpers";
import { IconButton } from "@/components/ui/icon-button";

// Lazy import to break the circular dependency with CreatePartnerModal
// (which imports this modal); a static cycle leaves one export undefined
// at render and crashes the host page.
const CreatePartnerModal = lazy(
  () => import("@/pages/partners/components/CreatePartnerModal")
);

interface Props {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  resetPage?: () => void;
  onSuccess?: (id: string, name: string) => void;
  defaultName?: string;
  defaultPartnerId?: string | null;
}

type FormValues = Record<string, unknown>;
type FormSetValue = (
  fieldName: string,
  value: unknown,
  options?: { shouldValidate?: boolean }
) => void;
type FormGetValues = () => FormValues;

const CreatePartnerContactModal: React.FC<Props> = ({
  isOpen,
  setIsOpen,
  resetPage,
  onSuccess,
  defaultName,
  defaultPartnerId
}) => {
  const { t } = useTranslation();
  const formRef = useRef<HTMLFormElement>(null);
  const createPartnerContact = useCreatePartnerContactMutation();

  const [formGetValues, setFormGetValues] = useState<FormGetValues | null>(
    null
  );
  const [formSetValue, setFormSetValue] = useState<FormSetValue | null>(null);
  const [isFormValid, setIsFormValid] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [view, setView] = useState<"partnerContact" | "partners">(
    "partnerContact"
  );

  const { data: entityFieldsResponse } = useEntityFields(
    EntityType.PARTNER_CONTACT
  );
  const users = useBoundStore((state) => state.users.entities);

  const formFieldsContext: CreateFormNewContext = {
    users,
    userData: {} as UserData,
    selectedCompanyId: "",
    selectedPersonId: "",
    setSelectedCompanyId: () => {},
    setSelectedPersonId: () => {},
    setHasUserEnteredDealName: () => {},
    autoPopulateDealName: () => {},
    setView: (nextView) => {
      if (nextView === "partners") {
        setView("partners");
      }
    },
    hasUserEnteredDealName: false,
    entityType: EntityType.PARTNER_CONTACT
  };

  const onPartnerCreation = (id: string, name: string) => {
    if (!id || !name) return;
    if (typeof formSetValue === "function") {
      formSetValue("partner", id, { shouldValidate: true });
    }
  };

  const baseSections = createFormNewSectionsFromAPI(
    entityFieldsResponse?.fields,
    t,
    formFieldsContext
  );

  const placeholderByFieldName: Record<string, string> = {
    firstName: t("people.createModal.firstNamePlaceholder"),
    lastName: t("people.createModal.lastNamePlaceholder"),
    emails: t("people.createModal.emailPlaceholder")
  };

  const formSections: FormSectionProps[] = baseSections.map((section) => ({
    ...section,
    fields: (section.fields ?? []).map((field: FormFieldNewType) => {
        const isNameField =
          field.name === "firstName" || field.name === "lastName";
        const placeholderOverride = field.name
          ? placeholderByFieldName[field.name]
          : undefined;
        return {
          ...field,
          labelPosition: "left",
          layout: isNameField ? "half" : "flex",
          ...(placeholderOverride && {
            fieldActionProps: {
              ...field.fieldActionProps,
              placeholder: placeholderOverride
            }
          }),
          ...(field.name === "firstName" && {
            label: t("people.createModal.name"),
            ...(defaultName ? { defaultValue: defaultName } : {})
          }),
          ...(field.name === "lastName" && { label: "" })
        };
      }
    )
  }));

  const { handleCloseAttempt, warningDialogProps } = useUnsavedChanges(
    () => formGetValues?.() || {},
    formSections
  );

  useEffect(() => {
    if (defaultName && typeof formSetValue === "function") {
      formSetValue("firstName", defaultName, { shouldValidate: true });
    }
  }, [defaultName, formSetValue]);

  // Pre-fill the partner when quick-creating from a record that already has one
  // (e.g. the Deal/Lead partner-contact picker), sparing a redundant re-select.
  useEffect(() => {
    if (defaultPartnerId && typeof formSetValue === "function") {
      formSetValue("partner", defaultPartnerId, { shouldValidate: true });
    }
  }, [defaultPartnerId, formSetValue]);

  useEffect(() => {
    if (isOpen) {
      setIsSubmitting(false);
    } else {
      formRef.current?.reset();
      setFormSetValue(null);
      setFormGetValues(null);
      setIsFormValid(false);
      setView("partnerContact");
    }
  }, [isOpen]);

  const handleSubmit = async (data: PartnerContactData) => {
    if (isSubmitting) {
      return;
    }
    setIsSubmitting(true);
    const transformFormData: PartnerContactData = {
      ...data,
      ...mapEmptyToNull(buildMapEmptyToNullObject(baseSections, data))
    };

    const contactFullName = getPartnerContactFullName(data);

    await createPartnerContact.mutateAsync(transformFormData, {
      onSuccess: async (response) => {
        await resetPage?.();
        onSuccess?.(response.id, contactFullName);
        toast.success(
          `${contactFullName} ${t("partnercontacts.createModal.success")}`,
          { duration: 5000 }
        );
        setIsOpen(false);
      },
      onError: () => {
        setIsSubmitting(false);
        toast.error(
          () => (
            <div className="flex items-center gap-2">
              <div>{t("partnercontacts.createModal.error")}</div>
              <IconButton
                size="md"
                onClick={() => toast.dismiss()}
                variant="iconNoBg"
              >
                <Close />
              </IconButton>
            </div>
          ),
          { icon: null }
        );
      }
    });
  };

  const handleCancel = () => {
    handleCloseAttempt(() => {
      setIsOpen(false);
    });
  };

  const handleCreateNew = () => {
    formRef.current?.requestSubmit();
  };

  const handleSetValue = (formMethods: {
    setValue: FormSetValue;
    getValues: FormGetValues;
  }) => {
    if (typeof formMethods.setValue === "function") {
      setFormSetValue(() => formMethods.setValue);
    }
    if (typeof formMethods.getValues === "function") {
      setFormGetValues(() => formMethods.getValues);
    }
  };

  return (
    <>
      <Modal
        open={isOpen}
        setIsOpen={createUnsavedChangesModalSetter(
          setIsOpen,
          handleCloseAttempt
        )}
        data-test="create-partner-contact-modal"
        title={
          <Text as="div" variant="lgSubtitleH1">
            {t("partnercontacts.createModal.title")}
          </Text>
        }
        content={
          <div
            className="flex flex-col gap-3 p-6 h-[60vh] overflow-y-auto"
            data-test="create-partner-contact-modal-content"
          >
            <FormNew
              sections={formSections}
              isFormValid={(isValid) => setIsFormValid(isValid)}
              onSubmit={handleSubmit}
              formRef={formRef}
              onSetValue={handleSetValue}
              data-test="create-partner-contact-modal-form"
            />
          </div>
        }
        footer={
          <div
            className="flex justify-end gap-2"
            data-test="create-partner-contact-modal-footer"
          >
            <Button
              data-test="create-partner-contact-modal-cancel-button"
              variant="secondary"
              onClick={handleCancel}
            >
              {t("cancel")}
            </Button>
            <Button
              data-test="create-partner-contact-modal-submit-button"
              disabled={!isFormValid || isSubmitting}
              onClick={handleCreateNew}
            >
              {t("partnercontacts.createModal.createNew")}
            </Button>
          </div>
        }
        contentContainerClassname="w-[640px]"
      />
      {view === "partners" && (
        <Suspense fallback={null}>
          <CreatePartnerModal
            isOpen
            setIsOpen={(isOpen) => {
              if (!isOpen) {
                setView("partnerContact");
              }
            }}
            onSuccess={onPartnerCreation}
          />
        </Suspense>
      )}
      <UnsavedChangesWarningDialog
        {...warningDialogProps}
        data-test="create-partner-contact-modal-unsaved-warning"
      />
    </>
  );
};

export default CreatePartnerContactModal;
